/**
 * BE#304 — decide when to kick a background refresh of the WA-event gate's
 * webhook list.
 *
 * The gate (`dispatchWaEventThunk`) builds `subscribedWebhookEvents` from the
 * local `DBWebhooksReducer` and skips posting an inbound event when no consumer
 * is interested. That list is eager-loaded ONCE at init and — before this — only
 * re-loaded when it had never loaded at all. So a webhook created out-of-instance
 * (e.g. via the public v1 API, exactly what a dev integration does) was never
 * seen: the gate evaluated a stale list missing the new webhook, `any` was false,
 * and the inbound event was dropped until a manual extension reload.
 *
 * This adds a TTL: once the list is older than `ttlMs`, the next inbound event
 * kicks a fire-and-forget refresh, so a newly-created webhook is honored
 * automatically within about a minute — no reload. Kept pure so the decision is
 * unit-testable without a store or a clock.
 */

/** Refresh the gate's webhook list at most once per minute. */
export const GATE_LIST_TTL_MS = 60_000;

export interface GateListState {
  itemsLoaded?: boolean;
  itemsLoading?: boolean;
}

export const shouldRefreshGateList = (
  state: GateListState | undefined,
  lastRefreshAt: number,
  now: number,
  ttlMs: number = GATE_LIST_TTL_MS,
): boolean => {
  // A load is already in flight — let it finish rather than stacking fetches.
  if (state?.itemsLoading) return false;
  // Never loaded → load now (this preserves the original boot safety-net).
  if (!state?.itemsLoaded) return true;
  // Loaded but stale → refresh so an out-of-instance webhook is picked up.
  return now - lastRefreshAt >= ttlMs;
};
